import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import { mangoVarieties } from '@/mocks/mangoes';
import FadeInSection from '@/components/base/FadeInSection';

export default function Mangoes() {
  const { openEnquiry } = useEnquiryModal();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/dasheri2.jpeg"
          bgAlt="Bassi Enterprises fresh mango varieties for bulk supply"
          bgTitle="Bassi Enterprises - Fresh Mango Varieties"
          breadcrumbLabel="Mangoes"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-sun-line"></i>
              Mango Season
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Fresh Mango Varieties
            </h1>
            <p className="text-background-200 text-base md:text-lg leading-relaxed">
              Hand-picked, orchard-fresh mangoes available for bulk supply. Choose from our graded varieties &mdash; each selected for quality and supplied fresh to retailers, restaurants, distributors and businesses.
            </p>
          </div>
        </InnerPageHero>

        {/* Varieties */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {mangoVarieties.map((mango, idx) => (
                <FadeInSection key={mango.slug} delay={idx * 80}>
                  <div className="group bg-background-50 border border-background-200 rounded-2xl overflow-hidden hover:border-primary-300 hover:shadow-md transition-all duration-300 h-full flex flex-col">
                    {/* Image */}
                    <Link to={`/mangoes/${mango.slug}`} className="block relative w-full aspect-[4/3] overflow-hidden bg-background-100">
                      <img
                        src={mango.images[0]}
                        alt={`${mango.displayName} - Bassi Enterprises`}
                        title={`${mango.displayName} - Fresh bulk supply`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold shadow-sm ${
                            mango.available
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-amber-500 text-background-50'
                          }`}
                        >
                          <i className={mango.available ? 'ri-checkbox-circle-fill text-xs' : 'ri-time-line text-xs'}></i>
                          {mango.availabilityNote}
                        </span>
                      </div>
                    </Link>

                    {/* Content */}
                    <div className="p-5 md:p-6 flex flex-col flex-1">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                          {mango.displayName}
                        </h2>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-accent-100 text-accent-800 text-[10px] font-semibold rounded-full whitespace-nowrap">
                          {mango.badge}
                        </span>
                      </div>
                      <p className="text-foreground-500 text-sm font-medium mb-3">{mango.tagline}</p>

                      {/* Grades + prices */}
                      <div className="space-y-1.5 mb-4">
                        {mango.grades.map((g) => (
                          <div key={g.label} className="flex items-center justify-between text-sm border-b border-background-100 pb-1.5">
                            <span className="text-foreground-600">{g.label}</span>
                            <span className="font-semibold text-foreground-900">{g.priceRange}</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-[11px] text-foreground-400 italic mb-4">
                        Indicative MRP &mdash; bulk rates on enquiry
                      </p>

                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-5">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-calendar-line text-primary-500"></i>
                          Shelf life: {mango.shelfLife}
                        </span>
                      </div>

                      <div className="mt-auto flex items-center gap-2.5">
                        <Link
                          to={`/mangoes/${mango.slug}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          View Details
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                        <button
                          onClick={() => openEnquiry(`${mango.displayName} (${mango.name})`)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-whatsapp-fill text-sm"></i>
                          Enquire
                        </button>
                      </div>
                    </div>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* Bulk CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-primary-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary-400 blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-accent-500 blur-3xl"></div>
          </div>
          <FadeInSection>
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
                Need Bulk Mango Supply?
              </h2>
              <p className="text-background-50/60 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Get competitive bulk pricing on graded mangoes. We supply to retailers, restaurants, distributors, pickle makers and quick-commerce companies.
              </p>
              <button
                onClick={() => openEnquiry('Bulk Mango Supply')}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                Request Bulk Quote
              </button>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />
    </div>
  );
}
